import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { sanitizeSearchFilter } from 'src/app/order/utils/sanitize-search-filter';
import { FdFieldBaseComponent } from '../fd-field/fd-field.base-component';

@Component({
  selector: 'fd-select',
  templateUrl: './fd-select.component.html',
  styleUrls: ['./fd-select.component.scss'],
  standalone: false,
})
export class FdSelectComponent extends FdFieldBaseComponent implements OnInit, OnDestroy {
  get searchable() {
    return this.field && this.field.searchable;
  }

  get relatedFormControl(): AbstractControl {
    return this.parentForm.get(this.field.controlName);
  }

  get hasError() {
    return this.relatedFormControl.errors !== null;
  }

  get errorMessages() {
    return Object.keys(this.field.messages)
      .filter((val) => this.relatedFormControl.errors[val])
      .map((key) => this.field.messages[key]);
  }

  get selectedItem() {
    return (this.relatedFormControl && this.relatedFormControl.value) || null;
  }

  @Input()
  public field: FdSelectConfig;

  @Input()
  public parentForm: UntypedFormGroup;

  @Output()
  public loadItemsFn = new EventEmitter<string>();

  @Output() public selectionChange = new EventEmitter();

  @Output() public selectClosed = new EventEmitter();

  @ViewChild('binding') public binding: ElementRef;

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  @ViewChild(MatSelect) matSelectRef: MatSelect;

  @Output()
  public infiniteScroll = new EventEmitter<void>();

  public limit = 10;
  public offset = 0;
  public controlName: string;
  public context: any;
  public label: string;
  public accessibilityLabel?: string;
  public placeholder?: string;
  public items: Item[];
  public hint?: string;
  public messages?: { [key: string]: string };

  public focusedItem?: Item;

  private filterDebounceHandle: ReturnType<typeof setTimeout> | null = null;
  private panelKeydownListener: ((e: KeyboardEvent) => void) | null = null;

  constructor() {
    super();
  }

  public onChange = (_: any) => {};
  public onTouched = () => {};

  public async ngOnInit() {
    if (this.loadItemsFn) {
      this.loadItemsFn.emit();
    }

    if (this.field && this.field.disabled) {
      this.relatedFormControl.disable();
    }
  }

  public getIfSelected() {
    if (this.field && this.field.items) {
      const selectedItems = this.field.items.filter((x) => x.selected);

      if (selectedItems && selectedItems.length) {
        this.relatedFormControl.setValue(selectedItems[0].value);
      }
    }
    return null;
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public onSelectionChange($event: any) {
    this.updateRequiredField($event);
    this.selectionChange.emit($event);
  }

  public ngOnDestroy(): void {
    this.clearFilterDebounce();
    this.cleanupPanelKeydownListener();
  }

  public onSelectOpened(): void {
    if (this.field?.searchable && this.field?.searchInputFirst && this.searchInput) {
      setTimeout(() => {
        this.searchInput.nativeElement.focus();
        this.setupPanelKeydownListener();
      });
    }
  }

  public onSelectClosed(): void {
    this.clearFilterDebounce();
    this.cleanupPanelKeydownListener();
    if (this.field?.searchInputCleanOnClose && this.searchInput) {
      this.searchInput.nativeElement.value = '';
    }
    this.selectClosed.emit();
  }

  public handleSearchKeydown(event: KeyboardEvent): void {
    this.handleSpace(event);

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      event.stopPropagation();
      this.activateFirstOptionAndFocusPanel();
    } else if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.selectActiveOption();
    }
  }

  public handleSearchInput(event: KeyboardEvent): void {
    if (this.field?.numericOnly) {
      const target = event.target as HTMLInputElement;
      const currentValue = target.value;
      const sanitizedValue = currentValue.replace(/[^0-9:]/g, '');

      if (currentValue !== sanitizedValue) {
        target.value = sanitizedValue;
        // Trigger change detection
        const inputEvent = new Event('input', { bubbles: true });
        target.dispatchEvent(inputEvent);
      }
    } else if (this.field?.lettersOnly) {
      const target = event.target as HTMLInputElement;
      const currentValue = target.value;
      const sanitizedValue = currentValue.replace(/[^a-zA-ZáàâãéèêíìîóòôõöúùûüçñÁÀÂÃÉÈÊÍÌÎÓÒÔÕÖÚÙÛÜÇÑ\s]/g, '');

      if (currentValue !== sanitizedValue) {
        target.value = sanitizedValue;
        // Trigger change detection
        const inputEvent = new Event('input', { bubbles: true });
        target.dispatchEvent(inputEvent);
      }
    }
  }

  public filter(event: KeyboardEvent): void {
    this.handleSpace(event);

    const navigationKeys = ['ArrowDown', 'ArrowUp', 'Enter', 'Escape', 'Tab'];
    if (navigationKeys.includes(event.key)) {
      return;
    }

    const target = event.target as HTMLInputElement;
    const searchValue = sanitizeSearchFilter(target.value);

    this.clearFilterDebounce();

    if (this.isEmptySearch(searchValue)) {
      this.loadItemsFn.emit('');
      return;
    }

    this.filterDebounceHandle = setTimeout(() => {
      this.loadItemsFn.emit(searchValue);
      this.filterDebounceHandle = null;
    }, 300);
  }

  private updateRequiredField($event: any) {
    if ($event && this.field?.items?.length > 0 && this.field?.required) {
      this.field.required = false;
    }
  }

  private handleSpace(event: KeyboardEvent) {
    // tslint:disable-next-line: deprecation
    if (event.keyCode === 32 || event.keyCode === 35 || event.keyCode === 36) {
      // do not propagate spaces to MatSelect, as this would select the currently active option
      event.stopPropagation();
    }
  }

  private focusPanel(): void {
    const panel = this.matSelectRef?.panel?.nativeElement;
    if (panel) {
      panel.focus();
    }
  }

  private activateFirstOptionAndFocusPanel(): void {
    const keyManager = (this.matSelectRef as any)?._keyManager;
    if (keyManager?.setFirstItemActive) {
      keyManager.setFirstItemActive();
    }
    this.focusPanel();
  }

  private selectActiveOption(): void {
    const panel: HTMLElement | undefined = this.matSelectRef?.panel?.nativeElement;
    if (!panel) return;
    const activeOption = panel.querySelector('mat-option.mat-active, mat-option.mat-mdc-option-active') as HTMLElement;
    const firstOption = panel.querySelector('mat-option:not([aria-disabled="true"])') as HTMLElement;
    (activeOption || firstOption)?.click();
  }

  private setupPanelKeydownListener(): void {
    this.cleanupPanelKeydownListener();

    const panel = this.matSelectRef?.panel?.nativeElement;
    if (!panel) return;

    this.panelKeydownListener = (event: KeyboardEvent) => {
      if (event.key !== 'ArrowUp') return;

      const keyManager = (this.matSelectRef as any)?._keyManager;
      if (keyManager?.activeItemIndex === 0) {
        event.preventDefault();
        event.stopPropagation();
        this.searchInput?.nativeElement.focus();
      }
    };

    panel.addEventListener('keydown', this.panelKeydownListener, true);
  }

  private cleanupPanelKeydownListener(): void {
    if (this.panelKeydownListener) {
      const panel = this.matSelectRef?.panel?.nativeElement;
      if (panel) {
        panel.removeEventListener('keydown', this.panelKeydownListener, true);
      }
      this.panelKeydownListener = null;
    }
  }

  private clearFilterDebounce(): void {
    if (this.filterDebounceHandle) {
      clearTimeout(this.filterDebounceHandle);
      this.filterDebounceHandle = null;
    }
  }

  private isEmptySearch(value: string): boolean {
    return !value || value.trim().length === 0;
  }
}

export class Item {
  public label: string;
  public value: any;
  public selected?: boolean;
  public markAll?: boolean;
  public additionalProperties?: any;
}

export interface FdSelectConfig {
  controlName: string;
  disabled?: boolean;
  required?: boolean;
  label: string;
  searchPlaceholder?: string;
  context?: any;
  messages?: { [key: string]: string };
  searchable?: boolean;
  searchInputFirst?: boolean;
  searchInputCleanOnClose?: boolean;
  numericOnly?: boolean;
  lettersOnly?: boolean;
  items: Item[];
}
